/*
  adg-filters — the shared filter/sort pipeline for artwork grids.

  Phase B step 3. adg-artgrid.js had this logic; byo-builder.js had only a
  search box. Both read the same artwork index, so there was no reason for
  the builder to offer less — a shopper filtering by artist on a collection
  page and then losing that ability inside Build Your Own is the kind of
  inconsistency the whole convergence is meant to remove.

  Deliberately NARROW. Only the parts that are genuinely identical live
  here: which items survive the current filter state, and in what order.
  Chips, URL sync, pagination and empty-state rendering stay with each grid
  because they differ (BYO has no URL to sync, no chips row today).

  DATA SHAPES. The design grids hold raw index rows (`t`, `a`, `o`, `y`);
  BYO normalises into `title` / `artist_name` / `image`. Rather than teach
  both to speak one dialect mid-refactor, the accessors below read either.
*/
(function (global) {
  'use strict';

  var coll = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  function cmp(a, b) { return coll.compare(a || '', b || ''); }

  function title(x)  { return x.t || x.title || ''; }
  function artist(x) { return x.a || x.artist_name || ''; }
  /* EVERY credited artist, not just the first.

     adg-index.js expands each row into `artists` (all names) AND `a` (the
     first, for display). The filter only ever read `a`, so a design credited
     "Michael Korfhage, Joel Anderson" was findable as Joel and invisible as
     Michael. Joel is the primary on roughly 2,540 of 3,103 designs, which made
     eleven of the twelve artists on the Meet Our Artists page effectively
     unfilterable — and that is the filter Joel wants "See all artwork" to use
     (Aug 15 and again Aug 17). The data was already there; only the accessor
     was narrow. Falls back to the single name for data shapes without the
     array, so BYO and the merged search rows are unaffected. */
  function creditedArtists(x) {
    if (x.artists && x.artists.length) return x.artists;
    var one = artist(x);
    return one ? [one] : [];
  }
  function shape(x)  { return x.o || x.orientation || 'portrait'; }
  function year(x)   { return parseInt(x.y || x.year, 10) || 0; }
  function colls(x)  { return x.c || x.collections || []; }
  // The artwork's descriptive keywords (its product's free-form tags, baked
  // into the index by scripts/build-artwork-index.py). This is what lets a
  // search for "yeti" or "lighthouse" find designs whose titles say neither
  // — Joel's accuracy complaint. Absent on pre-keyword index rows: harmless.
  function kw(x)     { return x.k || x.keywords || ''; }
  // Art-print price (index `pr`, also from the generator). `missing` decides
  // where a row with no price lands — pass the value that sinks it to the
  // END for the direction being sorted, so unknowns never lead the grid.
  function price(x, missing) {
    var p = parseFloat(x.pr != null ? x.pr : x.price);
    return isNaN(p) || p <= 0 ? missing : p;
  }
  // The design's ORIGINAL date, best truth available (ISO strings — string
  // comparison is date comparison): `d` is when the artwork entered ADG's
  // own catalogue (backend snapshot, real chronology 2016→2025); `pub` is
  // when it reached this store — which IS the original date for designs
  // newer than the snapshot. The artwork's year breaks ties inside the
  // 2016 system-migration block. (Justin, 08-08: the dates must be real.)
  function pubDate(x) { return x.d || x.pub || ''; }
  // Best-selling rank (index `bs`): Shopify's OWN best-selling order over
  // the art-prints collection, baked in by the generator — the same ranking
  // the product pages' native sort reads, so "Best selling" means one thing
  // storewide. Unranked rows sink to the end.
  function bestRank(x) {
    var b = parseInt(x.bs, 10);
    return isNaN(b) ? Infinity : b;
  }

  /* Search normalization: lowercase, punctuation stripped — so "x-ing"
     matches "xing" and "Benny's" matches "bennys". Each item is matched
     against a spaced haystack AND a space-collapsed one, so "lighthouse"
     finds data written as "light house" (Justin, 08-08). */
  function searchNorm(str) {
    return String(str || '').toLowerCase().replace(/[’'"‘`´\-–—.,:;!?()]/g, '');
  }

  /* Accepts a string (legacy) or an array of artists (multi-select). */
  function artistList(v) {
    if (!v) return [];
    return Array.isArray(v) ? v.filter(Boolean) : [v];
  }

  /* state: { q, artist (string|array), orient, sort }  — all optional.
     sort: '' (source order) | 'best' | 'az' | 'za' | 'new' | 'old' |
     'price-asc' | 'price-desc' */
  function filterAndSort(items, state) {
    var s = state || {};
    // EVERY word must match somewhere (title, artist, keywords) — "bigfoot
    // night" finds "Bigfoot At Night". One long phrase no longer has to
    // appear verbatim.
    var tokens = searchNorm(s.q || '').split(/\s+/).filter(Boolean);
    var artists = artistList(s.artist);

    var view = items.filter(function (a) {
      // Collection membership. BYO relied on this and never had it — see the
      // note in byo-builder.js fetchArtworks. Matched against the item's own
      // collection list, which both data shapes carry (c / collections).
      if (s.collection && colls(a).indexOf(s.collection) === -1) return false;
      if (artists.length) {
        var credited = creditedArtists(a);
        var hit = false;
        for (var ai = 0; ai < credited.length; ai++) {
          if (artists.indexOf(credited[ai]) !== -1) { hit = true; break; }
        }
        if (!hit) return false;
      }
      if (s.orient && shape(a) !== s.orient) return false;
      // Rows flagged m:1 were matched SERVER-side for this same query
      // (merged merch on the search page — the match may live in a product
      // description the grid cannot see). They skip the token test and let
      // the relevance ranking below place them.
      if (tokens.length && !a.m) {
        var hay = searchNorm(title(a) + ' ' + artist(a) + ' ' + kw(a));
        var haySquashed = hay.replace(/\s+/g, '');
        for (var ti = 0; ti < tokens.length; ti++) {
          if (hay.indexOf(tokens[ti]) === -1 && haySquashed.indexOf(tokens[ti]) === -1) return false;
        }
      }
      return true;
    });

    // "The correct things float to the top" (Justin, 08-08): while a query
    // is active and no explicit sort is chosen, rank by match quality —
    // phrase in the title beats every-word-in-title beats an artist match
    // beats a keywords-only match. Applies identically to every row in the
    // grid, designs and merchandise alike; ties keep their existing order.
    if (tokens.length && !s.sort) {
      var phrase = tokens.join(' ');
      var scored = view.map(function (a, i) {
        var t = searchNorm(title(a));
        var ar = searchNorm(artist(a));
        var sc = 0;
        if (t.indexOf(phrase) !== -1) sc += 8;
        if (t.slice(0, phrase.length) === phrase) sc += 3;
        for (var si = 0; si < tokens.length; si++) {
          if (t.indexOf(tokens[si]) !== -1) sc += 2;
          if (ar && ar.indexOf(tokens[si]) !== -1) sc += 2;
        }
        return { a: a, sc: sc, i: i };
      });
      scored.sort(function (x, y) { return y.sc - x.sc || x.i - y.i; });
      view = scored.map(function (row) { return row.a; });
      return view;
    }

    switch (s.sort) {
      case 'az':  view.sort(function (x, y) { return cmp(title(x), title(y)); }); break;
      case 'za':  view.sort(function (x, y) { return cmp(title(y), title(x)); }); break;
      // Date added first, artwork year second, title last — so this week's
      // releases lead, the bulk-imported mass orders by how new the art
      // itself is, and equal designs never shuffle between renders.
      case 'new': view.sort(function (x, y) { return cmp(pubDate(y), pubDate(x)) || year(y) - year(x) || cmp(title(x), title(y)); }); break;
      // Ascending must not let dateless rows (merged-search merch) lead the
      // grid — same "unknowns never lead" rule the price sorts follow.
      case 'old': view.sort(function (x, y) { return cmp(pubDate(x) || '9999', pubDate(y) || '9999') || year(x) - year(y) || cmp(title(x), title(y)); }); break;
      case 'best': view.sort(function (x, y) { return bestRank(x) - bestRank(y) || cmp(title(x), title(y)); }); break;
      case 'price-asc':  view.sort(function (x, y) { return price(x, Infinity) - price(y, Infinity) || cmp(title(x), title(y)); }); break;
      case 'price-desc': view.sort(function (x, y) { return price(y, -Infinity) - price(x, -Infinity) || cmp(title(x), title(y)); }); break;
      default: break;   // '' = leave the source order alone
    }
    return view;
  }

  /* The artist dropdown is built from the data actually present, so a
     collection never offers an artist with nothing behind it. */
  function artistsOf(items) {
    var seen = Object.create(null);
    items.forEach(function (a) { creditedArtists(a).forEach(function (n) { if (n) seen[n] = true; }); });
    return Object.keys(seen).sort(cmp);
  }

  function hasBothShapes(items) {
    var p = false, l = false;
    for (var i = 0; i < items.length; i++) {
      if (shape(items[i]) === 'landscape') l = true; else p = true;
      if (p && l) return true;
    }
    return false;
  }

  /* ONE rulebook for what the filter row offers. Every grid renders the same
     snippet (adg-filter-row.liquid, data-df-* hooks) and calls this with the
     rows in scope; the rules live here so a control can never behave one way
     on a design page and another way inside a builder. The rules:

       artist  — >1 artist: dropdown with per-artist counts. Exactly 1: the
                 dropdown would be a dead control, but the cards stopped
                 printing artist names (Joel, email 1 item 5), so the name
                 becomes a plain byline in the same slot — hiding it would
                 remove information, not just a control. 0: hidden.
       shape   — shown only when the scope holds both shapes. All-portrait
                 collections (calendars by rule, others by accident) get no
                 dead dropdown.
       search  — a search box over eleven designs is noise; over six hundred
                 it is the fastest way in. Threshold: 12.
       sort    — pointless below two items.

     IDEMPOTENT ON PURPOSE: the builders re-run it every time the shopper
     switches collection, so it resets everything it touched before deciding
     again. The design grids call it once; running twice must be harmless.

     els:   { q, qWrap, artist, artistWrap, orient, orientWrap, sort, sortWrap }
            — any of them absent is skipped.
     items: the rows in scope (either data shape).
     state: { q, artist, orient, sort } current selections.
     Returns the normalized state: a selection the new scope cannot honour
     comes back cleared, and the caller must adopt what it gets. */
  function populateRow(els, items, state) {
    var s = {
      q:      (state && state.q)      || '',
      // ALWAYS an array from here out — the dropdown is an "add an artist"
      // control (pick one, it becomes a chip, the dropdown resets, pick
      // another). Selections the new scope cannot honour fall away.
      artist: artistList(state && state.artist),
      orient: (state && state.orient) || '',
      sort:   (state && state.sort)   || ''
    };

    if (els.artist && els.artistWrap) {
      els.artist.innerHTML = '<option value="">All artists</option>';
      els.artist.hidden = false;
      els.artistWrap.classList.remove('adg-coll__field--static');
      var oldByline = els.artistWrap.querySelector('.adg-coll__static');
      if (oldByline) oldByline.parentNode.removeChild(oldByline);
      els.artistWrap.hidden = true;

      var counts = Object.create(null);
      items.forEach(function (x) { creditedArtists(x).forEach(function (n) { if (n) counts[n] = (counts[n] || 0) + 1; }); });
      var names = Object.keys(counts).sort(cmp);
      if (names.length > 1) {
        names.forEach(function (n) {
          var o = document.createElement('option');
          o.value = n;
          o.textContent = n + ' (' + counts[n] + ')';
          els.artist.appendChild(o);
        });
        s.artist = s.artist.filter(function (n) { return !!counts[n]; });
        els.artist.value = '';   // the placeholder; active picks live as chips
        els.artistWrap.hidden = false;
      } else if (names.length === 1) {
        els.artist.hidden = true;
        els.artistWrap.classList.add('adg-coll__field--static');
        var byline = document.createElement('span');
        byline.className = 'adg-coll__static';
        byline.textContent = 'Art by ' + names[0];
        els.artistWrap.appendChild(byline);
        els.artistWrap.hidden = false;
        s.artist = [];
      } else {
        s.artist = [];
      }
    }

    if (els.orient && els.orientWrap) {
      // Always visible (Justin, 08-07): the row reads the same on every
      // page. It offers only the shapes the scope actually holds — an
      // all-portrait collection shows Portrait alone rather than losing
      // the control entirely (the old rule hid the whole dropdown, which
      // read as "this page is missing something").
      var hasP = false, hasL = false;
      for (var si = 0; si < items.length; si++) {
        if (shape(items[si]) === 'landscape') hasL = true; else hasP = true;
        if (hasP && hasL) break;
      }
      for (var ooi = 0; ooi < els.orient.options.length; ooi++) {
        var oo = els.orient.options[ooi];
        if (oo.value === 'portrait')  { oo.hidden = !hasP; oo.disabled = !hasP; }
        if (oo.value === 'landscape') { oo.hidden = !hasL; oo.disabled = !hasL; }
      }
      if ((s.orient === 'portrait' && !hasP) || (s.orient === 'landscape' && !hasL)) s.orient = '';
      els.orient.value = s.orient || '';
      els.orientWrap.hidden = items.length === 0;
    }

    if (els.q && els.qWrap) {
      // Always offered. There used to be a hide-under-13 rule; Justin
      // overruled it 08-07 — someone dropping into a small collection may
      // still reach for search, and an in-memory filter costs nothing.
      els.q.value = s.q;
      els.qWrap.hidden = false;
    }

    if (els.sort && els.sortWrap) {
      if (items.length > 1) {
        // Price options obey the same rule as the shape dropdown: offered
        // only when the scope can actually be reordered by them. Today every
        // art print is one price ($21.99 across all 2,976 — measured
        // 08-07), so a visible price sort would do nothing and read as
        // broken. The moment the catalogue carries two prices, these
        // options surface everywhere on their own — no code change.
        var pLo = Infinity, pHi = -Infinity;
        for (var pi = 0; pi < items.length; pi++) {
          var pv = price(items[pi], null);
          if (pv === null) continue;
          if (pv < pLo) pLo = pv;
          if (pv > pHi) pHi = pv;
        }
        var priceVaries = pHi > pLo;
        // Best selling follows the same gate: offered only when the scope
        // actually carries ranks (a pre-`bs` index row set would make the
        // option a silent no-op).
        var hasBest = false;
        for (var bi = 0; bi < items.length; bi++) {
          if (bestRank(items[bi]) !== Infinity) { hasBest = true; break; }
        }
        for (var oi = 0; oi < els.sort.options.length; oi++) {
          var opt = els.sort.options[oi];
          if (opt.value.indexOf('price-') === 0) {
            opt.hidden = !priceVaries;
            opt.disabled = !priceVaries;
          }
          if (opt.value === 'best') {
            opt.hidden = !hasBest;
            opt.disabled = !hasBest;
          }
        }
        if (!priceVaries && s.sort.indexOf('price-') === 0) s.sort = '';
        if (!hasBest && s.sort === 'best') s.sort = '';
        els.sort.value = s.sort || '';
        if (els.sort.value !== s.sort) s.sort = '';
        els.sortWrap.hidden = false;
      } else {
        els.sortWrap.hidden = true;
        s.sort = '';
      }
    }

    return s;
  }

  /* ONE chips renderer for every grid (design pages had their own; the
     builders had none — Justin, 08-08: the pills belong everywhere the row
     is). Mutates `state` when a chip is removed, resets the matching input,
     then calls `apply` so the host re-filters. One chip per active filter,
     one chip PER SELECTED ARTIST, Clear all when two or more. */
  function renderChips(chipsEl, els, state, apply) {
    if (!chipsEl) return;
    chipsEl.innerHTML = '';
    var active = [];
    if (state.q) active.push({ label: 'Search', value: state.q, remove: function () {
      state.q = ''; if (els.q) els.q.value = '';
    } });
    artistList(state.artist).forEach(function (name) {
      active.push({ label: 'Artist', value: name, remove: function () {
        state.artist = artistList(state.artist).filter(function (n) { return n !== name; });
      } });
    });
    if (state.orient) active.push({
      label: 'Shape',
      value: state.orient === 'landscape' ? 'Landscape' : 'Portrait',
      remove: function () { state.orient = ''; if (els.orient) els.orient.value = ''; }
    });
    if (!active.length) { chipsEl.hidden = true; return; }

    /* Removing a chip deletes the focused button; put focus somewhere useful
       (WCAG 2.4.3, RC check B M3): the chip now in the same place, else the
       one before it, else the control that set the filter. Runs after the
       host re-renders the chips. */
    function refocus(idx, fallback) {
      setTimeout(function () {
        var chips = chipsEl.querySelectorAll('button');
        var next = chips[idx] || chips[idx - 1] || fallback || els.q || els.artist || els.orient;
        if (next && next.focus && document.contains(next)) next.focus();
      }, 150);   // after the host's re-filter has re-rendered the chips
    }

    active.forEach(function (f, idx) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'adg-designs__chip';
      b.innerHTML = '<span>' + f.label + ': <b></b></span><i aria-hidden="true">&times;</i>';
      b.querySelector('b').textContent = f.value;
      // Label in Name (2.5.3): the name starts with the visible text "Artist: Joel".
      b.setAttribute('aria-label', f.label + ': ' + f.value + ', remove filter');
      b.addEventListener('click', function () {
        var fallback = f.label === 'Search' ? els.q : (f.label === 'Artist' ? els.artist : els.orient);
        f.remove(); apply(); refocus(idx, fallback);
      });
      chipsEl.appendChild(b);
    });

    if (active.length > 1) {
      var clear = document.createElement('button');
      clear.type = 'button';
      clear.className = 'adg-designs__chip adg-designs__chip--clear';
      clear.textContent = 'Clear all';
      clear.addEventListener('click', function () {
        state.q = ''; state.artist = []; state.orient = '';
        if (els.q) els.q.value = '';
        if (els.artist) els.artist.value = '';
        if (els.orient) els.orient.value = '';
        apply();
        refocus(-1, els.q || els.artist || els.orient);
      });
      chipsEl.appendChild(clear);
    }
    chipsEl.hidden = false;
  }

  global.ADGFilters = {
    filterAndSort: filterAndSort,
    collectionsOf: colls,
    artistsOf: artistsOf,
    artistList: artistList,
    hasBothShapes: hasBothShapes,
    populateRow: populateRow,
    renderChips: renderChips,
    // The one date rule. The "New" badges read this SAME accessor the Date
    // sorts rank by, so the badged cards are by construction the head of
    // "new to old" and the tail of "old to new" — never scattered.
    pubDate: pubDate,
    cmp: cmp
  };
})(window);
